import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import { constants, log } from "@qgiv/core-js";
import { getCardToken } from "@qgiv/core-react";
import {
    SEO,
    UpdateFieldErrorFocus,
    asyncSessionStorage,
    getSessionStorageKey,
    scrubPaymentKeysFromValues,
} from "@qgiv/core-donor";
import {
    submitRegistration,
    prepareValuesForP2PRegistrationSubmit,
} from "@qgiv/p2p-registration";
import { useDispatch } from "react-redux";
import {
    getInitialTouched,
    getInitialValues,
    getValidationSchema,
} from "./formik";
import RegistrationBase from "../templates/RegistrationBase";
// eslint-disable-next-line import/no-cycle
import ConnectedPaymentPageContent from "./ConnectedPaymentPageContent";
import ConnectedUpdateStoreCart from "../StorePage/ConnectedUpdateStoreCart";
import ErrorDisplay from "../../common/ErrorDisplay";

const PaymentPage = ({
    baseUrl = "",
    classification = {},
    currentPage,
    discounts = {},
    erroredEntities = [],
    fetchReceipt,
    hasUnconfirmedRegistrations = false,
    payment = {},
    formDataValues = {},
    promoCodes,
    redirect = "",
    registrations,
    registrationHasCommitment = false,
    setAppHasSubmittedRegistration,
    setErroredEntities,
    setOutdated,
    setUpdated,
    settings,
    storeCart,
    updateRegistrationAccount,
    validationSettings,
    documentTitle,
    parentRegistrationId,
    shippingMethodAmount = 0,
    totals,
    updateCurrentPage,
    updatePayment,
    urlOfPaymentIframe,
    sessionStorageReduxData = {},
}) => {
    const {
        ENUMS: {
            DefaultView: {
                REGISTRATION_CHECKOUT_STEP,
                STANDARD_REGISTRATION_RECEIPT,
            },
            PaymentType,
        },
    } = constants;

    const dispatch = useDispatch();

    const {
        formSettings: { id },
    } = settings;
    const [receiptId, setReceiptId] = useState(null);
    const [toReceipt, setToReceipt] = useState(false);
    const [errors, setErrors] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        if (redirect) {
            navigate(redirect);
        }
    }, [redirect, navigate]);

    useEffect(() => {
        if (toReceipt && receiptId) {
            navigate(`${baseUrl}/${receiptId}/receipt`);
            dispatch(updateCurrentPage(STANDARD_REGISTRATION_RECEIPT));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toReceipt, receiptId, navigate]);

    useEffect(() => {
        if (currentPage !== REGISTRATION_CHECKOUT_STEP) {
            dispatch(updateCurrentPage(REGISTRATION_CHECKOUT_STEP));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const sessionStorageKey = getSessionStorageKey(id);

    const handleSubmitRegistrationSuccess = (response, formikBag) => {
        const { data } = response;
        const { setSubmitting } = formikBag;
        const { success } = data;

        // remove form data from session storage
        // comment out below line for local testing so that data persists in sessionStorage
        asyncSessionStorage.remove(sessionStorageKey);
        // 3. Set `submitting` to false
        setSubmitting(false);
        // 4. Handle response if successful
        if (success) {
            const {
                response: {
                    registrationAccount,
                    feeCoverage,
                    transaction,
                    total,
                },
            } = success;
            // eslint-disable-next-line no-shadow
            const { receiptId } = transaction;
            const receipt = {
                feeCoverage,
                total,
                transaction,
            };
            // Store registration account data in redux store
            dispatch(updateRegistrationAccount(registrationAccount));
            // Store receipt data in redux store
            dispatch(fetchReceipt({ receipt }));
            // Update flag used by analytics logic
            dispatch(setAppHasSubmittedRegistration(true));
            // Navigate user to receipt
            setReceiptId(receiptId);
            setToReceipt(true);
            // Scroll to top of window only after toReceipt is true
            window.scrollTo(0, 0);
        }
    };

    const handleSubmitRegistrationError = (error, formikBag) => {
        const {
            outdated = [],
            updated = [],
            // eslint-disable-next-line no-shadow
            erroredEntities = [],
            message = ["An error occurred"],
        } = error;
        const { setSubmitting } = formikBag;

        if (typeof message === "string") {
            const messageData = [];
            messageData.push(message);
            setErrors(messageData);
        } else {
            setErrors(message);
        }
        setSubmitting(false);

        if (outdated.length > 0) {
            dispatch(setOutdated([...outdated]));
            if (Object.keys(updated).length > 0) {
                dispatch(setUpdated(updated));
            }
        }
        if (erroredEntities.length > 0) {
            dispatch(setErroredEntities(erroredEntities));
        }
        // Scroll to top of window only if there is a BE error
        window.scrollTo(0, 0);
    };

    const handleUnconfirmedRegistrations = (formikBag) => {
        const { setSubmitting } = formikBag;

        setErrors(["Please review + confirm all registration details"]);
        setSubmitting(false);
    };

    const handleConfirmedRegistrations = (values, formikBag) => {
        // 1. Assemble data for form submission
        const submitValues = prepareValuesForP2PRegistrationSubmit(
            settings,
            classification,
            values,
            discounts,
            promoCodes,
            registrations,
            storeCart,
            parentRegistrationId,
            shippingMethodAmount,
            totals,
        );

        // Remove all of the sensitive payment data and store this data in
        // session storage and Redux
        const scrubbedValuesObject = scrubPaymentKeysFromValues(values);
        const sessionStorageData = {
            ...sessionStorageReduxData,
            values: scrubbedValuesObject,
        };
        dispatch(updatePayment(scrubbedValuesObject));

        asyncSessionStorage
            .set(sessionStorageKey, sessionStorageData)
            .then(() => {
                // 2. Send data to form submission endpoint
                submitRegistration(id, submitValues)
                    .then((response) =>
                        handleSubmitRegistrationSuccess(response, formikBag),
                    )
                    .catch((error) =>
                        handleSubmitRegistrationError(error, formikBag),
                    );
            })
            .catch(() => {
                // need to call submitRegistration in catch block
                // since this will throw an error if we don't have
                // access to session storage. This is the expected
                // behavior when in Incognito mode on embedded forms on external sites.
                submitRegistration(id, submitValues)
                    .then((response) =>
                        handleSubmitRegistrationSuccess(response, formikBag),
                    )
                    .catch((error) =>
                        handleSubmitRegistrationError(error, formikBag),
                    );
            });
    };

    const getShouldSubmitRegistration = (values, formikBag) => {
        const { total } = totals;
        const { Payment_Type } = values;
        const { setSubmitting } = formikBag;
        const requiresPaymentInformation =
            total > 0 || registrationHasCommitment;
        const requiresCardTokenFromPaymentIframe =
            Payment_Type === PaymentType.CREDITCARD &&
            requiresPaymentInformation;

        // Abort the attempt to submit a registration if one of the fields in the
        // payment iframe throws a validation error or if an unexpected error occurs
        // when attempting to generate a new card token
        const shouldSubmitRegistration = new Promise((resolve, reject) => {
            const handleCardTokenSuccess = (response) => {
                const { validationErrors, token } = response;
                const hasPaymentIframeValidationErrors =
                    Object.keys(validationErrors).length > 0;

                // Instead of attempting to set the value of a field in Formik
                // return a promise that also contains the card token as we
                // need to be able to manually insert the the card token into
                // the list of values used for submission.
                const resultWithToken = {
                    token,
                };

                if (hasPaymentIframeValidationErrors) {
                    setSubmitting(false);
                    resolve({
                        ...resultWithToken,
                        shouldSubmitRegistration: false,
                    });
                } else {
                    resolve({
                        ...resultWithToken,
                        shouldSubmitRegistration: true,
                    });
                }
            };

            const handleCardTokenError = (response) => {
                setSubmitting(false);
                reject(response);
            };

            getCardToken(urlOfPaymentIframe, requiresCardTokenFromPaymentIframe)
                .then(handleCardTokenSuccess)
                .catch(handleCardTokenError);
        });

        return shouldSubmitRegistration;
    };

    // Insert the the card token into the list of values used for submission
    // so that it is available to us when we send the submission API request to
    // the BE. Necessary because the fact that the logic that contains the API
    // request lives outside Formik means that we will not be able to access
    // updated Formik values until after the request has been sent.
    const onSubmit = (values, formikBag) => {
        if (hasUnconfirmedRegistrations) {
            handleUnconfirmedRegistrations(formikBag);
        } else {
            getShouldSubmitRegistration(values, formikBag)
                .then(({ shouldSubmitRegistration, token }) => {
                    if (shouldSubmitRegistration) {
                        const Card_Token = token;
                        const valuesWithToken = {
                            ...values,
                            Card_Token,
                        };
                        handleConfirmedRegistrations(
                            valuesWithToken,
                            formikBag,
                        );
                    }
                })
                // eslint-disable-next-line no-shadow
                .catch((errors) => {
                    log(errors);
                });
        }
    };

    const savedPaymentValues = payment;
    const formikProps = {
        initialTouched: getInitialTouched(settings, savedPaymentValues),
        initialValues: getInitialValues(
            settings,
            formDataValues,
            savedPaymentValues,
        ),
        onSubmit,
        validationSchema: getValidationSchema(validationSettings),
    };
    return (
        <Formik {...formikProps}>
            <RegistrationBase>
                <SEO title={documentTitle} description="" />
                <ErrorDisplay
                    errors={errors}
                    setErrors={setErrors}
                    erroredEntities={erroredEntities}
                />
                <Form>
                    <ConnectedPaymentPageContent />
                    <UpdateFieldErrorFocus />
                    <ConnectedUpdateStoreCart />
                </Form>
            </RegistrationBase>
        </Formik>
    );
};

PaymentPage.propTypes = {
    // Saved classification page values
    classification: PropTypes.shape({}),
    // Redux store value for current page enum
    currentPage: PropTypes.number.isRequired,
    // Object containing the Promo code data
    discounts: PropTypes.shape({}),
    // Array of outdated entities used for Error Redirect
    erroredEntities: PropTypes.arrayOf(PropTypes.shape({})),
    // Redux action to update stored receipt object
    fetchReceipt: PropTypes.func.isRequired,
    // Flag indicating that user needs to confirm registrations before submitting
    hasUnconfirmedRegistrations: PropTypes.bool,
    // Saved payment values
    payment: PropTypes.shape({}),
    // Stored promo codes to apply
    promoCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
    // Optional redirect object
    redirect: PropTypes.string,
    // Saved per-participant registration values
    registrations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    // One of the registrations has a fundraising commitment
    registrationHasCommitment: PropTypes.bool.isRequired,
    // Redux action to update has submitted registration flag
    setAppHasSubmittedRegistration: PropTypes.func.isRequired,
    // Redux action to dispatch when submission returns outdated entities array
    setOutdated: PropTypes.func.isRequired,
    // Redux action to dispatch when submission returns updated data array
    setUpdated: PropTypes.func.isRequired,
    // Redux action to set which entities were outdated
    setErroredEntities: PropTypes.func.isRequired,
    // Settings object with form and event settings
    settings: PropTypes.shape({
        formSettings: PropTypes.shape({ id: PropTypes.number }),
    }).isRequired,
    // Saved shipping method amount value
    shippingMethodAmount: PropTypes.number,
    // Saved array of items added to store cart values
    storeCart: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    // Redux action to update stored registration account
    updateRegistrationAccount: PropTypes.func.isRequired,
    // Redux action to update current page by enum
    updateCurrentPage: PropTypes.func.isRequired,
    // Redux action to update payment in registration
    updatePayment: PropTypes.func.isRequired,
    // URL of the payment iframe we need to get the card token from
    urlOfPaymentIframe: PropTypes.string.isRequired,
    // Object to be passed to validation logic
    validationSettings: PropTypes.shape({
        matchingRatio: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
    }).isRequired,
    // String that contains the document title that appears in the browser tab
    documentTitle: PropTypes.string.isRequired,
    // Id of the parent registration. If there is not a parent registration, will be "0"
    parentRegistrationId: PropTypes.string.isRequired,
    // Object containing all totals from summary. Source of truth for totals
    totals: PropTypes.shape({}).isRequired,
    baseUrl: PropTypes.string,
    formDataValues: PropTypes.shape({}),
    sessionStorageReduxData: PropTypes.shape({}),
};

export default PaymentPage;
